import type {
  RegionalHighlightListingStatus,
  RegionalHighlightListingStatusesByListingId,
} from '@/types/regionalHighlight';

const isRegionalHighlightListingStatus = (
  value: unknown,
): value is RegionalHighlightListingStatus =>
  typeof value === 'object' &&
  value !== null &&
  'hasRegionalHighlight' in value &&
  typeof (value as RegionalHighlightListingStatus).hasRegionalHighlight === 'boolean';

const isStatusesMap = (value: unknown): value is RegionalHighlightListingStatusesByListingId => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  return Object.values(value).every(
    (entry) => entry === undefined || isRegionalHighlightListingStatus(entry),
  );
};

const statusesFromListingArray = (
  listings: unknown,
): RegionalHighlightListingStatusesByListingId => {
  if (!Array.isArray(listings)) {
    return {};
  }

  return listings.reduce<RegionalHighlightListingStatusesByListingId>((acc, entry) => {
    if (
      typeof entry !== 'object' ||
      entry === null ||
      !('listingId' in entry) ||
      typeof (entry as { listingId: unknown }).listingId !== 'string' ||
      !isRegionalHighlightListingStatus(entry)
    ) {
      return acc;
    }

    const { listingId, ...status } = entry as RegionalHighlightListingStatus & {
      listingId: string;
    };
    acc[listingId] = status;

    return acc;
  }, {});
};

export const normalizeRegionalHighlightListingStatuses = (
  responseBody: unknown,
): RegionalHighlightListingStatusesByListingId => {
  if (typeof responseBody !== 'object' || responseBody === null) {
    return {};
  }

  const body = responseBody as Record<string, unknown>;
  const data = body.data;

  if (isStatusesMap(body.statuses)) {
    return body.statuses;
  }

  if (typeof data === 'object' && data !== null) {
    const dataRecord = data as Record<string, unknown>;

    if (isStatusesMap(dataRecord.statuses)) {
      return dataRecord.statuses;
    }

    if (Array.isArray(dataRecord.listings)) {
      return statusesFromListingArray(dataRecord.listings);
    }

    if (isStatusesMap(dataRecord)) {
      return dataRecord;
    }
  }

  if (Array.isArray(body.listings)) {
    return statusesFromListingArray(body.listings);
  }

  return {};
};
